import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  MessageCircle, 
  ArrowLeft, 
  Share2, 
  Clock, 
  MapPin,
  User,
  Calendar,
  BookOpen,
  Filter
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getPosts, getComments, createComment, likePost, unlikePost } from '../services/supabase';
import toast from 'react-hot-toast';
import LoadingSkeleton from '../components/UI/LoadingSkeleton';

const PostsPage = () => {
  const { id } = useParams();
  const { isAuthenticated, requireAuth, profile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: 'created_at',
    search: ''
  });

  useEffect(() => {
    fetchPosts();
  }, [id, filters]);

  useEffect(() => {
    if (id && posts.length > 0) {
      const post = posts.find(p => p.id === id);
      if (post) {
        setCurrentPost(post);
        setShowComments(true);
      }
    }
  }, [id, posts]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const options = {
        limit: 20,
        sortBy: filters.sortBy,
        search: filters.search
      };
      
      const { data: postsData } = await getPosts(options);
      
      if (id) {
        // Single post view
        const singlePost = postsData?.find(p => p.id === id);
        setPosts(singlePost ? [singlePost] : []);
      } else {
        // Posts list view
        setPosts(postsData || []);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const { data: commentsData } = await getComments(postId);
      setComments(commentsData || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleLike = async (postId) => {
    if (!requireAuth(() => {})) return;

    try {
      const post = posts.find(p => p.id === postId);
      const isLiked = post?.isLiked || false;
      
      if (isLiked) {
        await unlikePost(postId, profile?.id || 'user-id');
        setPosts(prev => prev.map(p => 
          p.id === postId 
            ? { ...p, likes: Math.max(0, (p.likes || 0) - 1), isLiked: false }
            : p
        ));
        toast.success('Like removed');
      } else {
        await likePost(postId, profile?.id || 'user-id');
        setPosts(prev => prev.map(p => 
          p.id === postId 
            ? { ...p, likes: (p.likes || 0) + 1, isLiked: true }
            : p
        ));
        toast.success('Post liked!');
      }
    } catch (error) {
      console.error('Failed to update like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleComment = async (e, postId) => {
    e.preventDefault();
    if (!requireAuth(() => {})) return;

    if (!newComment.trim()) return;

    try {
      const { data: commentData } = await createComment({
        post_id: postId,
        text: newComment.trim(),
        parent_comment_id: null
      });
      
      if (commentData) {
        setComments(prev => [commentData, ...prev]);
        setNewComment('');
        toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleShare = async (post) => {
    const shareData = {
      title: post.title,
      text: `Read "${post.title}" on Historer`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <LoadingSkeleton type="story" count={1} />
          <div className="mt-8">
            <LoadingSkeleton type="card" count={3} />
          </div>
        </div>
      </div>
    );
  }

  // Single post view
  if (id && currentPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Navigation */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link
              to="/posts"
              className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Posts</span>
            </Link>
          </motion.div>

          {/* Post Content */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
          >
            {/* Post Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {currentPost.author?.username || 'Anonymous'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {currentPost.created_at ? new Date(currentPost.created_at).toLocaleDateString() : 'Unknown date'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleLike(currentPost.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                      currentPost.isLiked
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${currentPost.isLiked ? 'fill-current' : ''}`} />
                    <span className="text-sm">{currentPost.likes || 0}</span>
                  </button>
                  
                  <button
                    onClick={() => setShowComments(!showComments)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                      showComments
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm">{comments.length}</span>
                  </button>
                  
                  <button
                    onClick={() => handleShare(currentPost)}
                    className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <h1 className="text-3xl font-serif font-bold text-gray-900 mb-4">
                {currentPost.title}
              </h1>
              
              {currentPost.image && (
                <div className="mb-6">
                  <img
                    src={currentPost.image}
                    alt={currentPost.title}
                    className="w-full h-64 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x400?text=No+Image'
                    }}
                  />
                </div>
              )}
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {currentPost.content || 'No content available'}
                </p>
              </div>
            </div>

            {/* Comments Section */}
            {showComments && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="border-t border-gray-200"
              >
                {/* Comment Form */}
                {isAuthenticated && (
                  <form onSubmit={(e) => handleComment(e, currentPost.id)} className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave a Comment</h3>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts..."
                      className="w-full p-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                    />
                    <button
                      type="submit"
                      disabled={!newComment.trim()}
                      className="mt-3 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Post Comment
                    </button>
                  </form>
                )}

                {/* Comments List */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Comments ({comments.length})
                  </h3>
                  
                  {comments.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No comments yet. Be the first to share your thoughts!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {comment.author?.username || 'Anonymous'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {comment.created_at ? new Date(comment.created_at).toLocaleDateString() : 'Just now'}
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700">{comment.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.article>
        </div>
      </div>
    );
  }

  // Posts list view
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-serif font-bold text-gray-900 mb-4">
            Community Posts
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover stories and insights shared by our community of history enthusiasts.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Sort by:</label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="created_at">Recent</option>
                <option value="likes">Popular</option>
                <option value="title">Title A-Z</option>
              </select>
            </div>
            
            {isAuthenticated && (
              <Link
                to="/create-post"
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Create Post
              </Link>
            )}
          </div>
        </motion.div>

        {/* Posts Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {posts.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Posts Yet</h3>
              <p className="text-gray-600 mb-6">
                Be the first to share a historical story with the community!
              </p>
              {isAuthenticated ? (
                <Link
                  to="/create-post"
                  className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Create First Post
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <User className="w-5 h-5 mr-2" />
                  Join Community
                </Link>
              )}
            </div>
          ) : (
            posts.map((post, index) => (
              <motion.div
                key={post.id}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="group cursor-pointer"
              >
                <Link to={`/posts/${post.id}`}>
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full hover:shadow-xl transition-shadow">
                    <div className="relative h-48 overflow-hidden">
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x200?text=No+Image'
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-purple-600" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-gray-900">
                        <User className="w-3 h-3 inline mr-1" />
                        {post.author?.username || 'Anonymous'}
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-serif font-semibold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.content || 'No content available'}
                      </p>
                      
                      <div className="flex items-center justify-between text-gray-500 text-sm">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span>{post.likes || 0}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageCircle className="w-4 h-4" />
                            <span>{post.comments?.length || 0}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : 'Unknown'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PostsPage;
